import { useMemo } from 'react';
import { Copy, Mail, MessageSquare, Sparkles } from 'lucide-react';
import type { Member, MemberPublic, PostEnriched } from '@/lib/db/types';
import { buildRoundRecap } from '@/lib/round-recap';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  post: PostEnriched;
  me: Member;
  memberMap: Record<string, MemberPublic>;
  onClose: () => void;
}

function scoreLabel(score: number, par: number): string {
  const d = score - par;
  if (d === 0) return 'E';
  return d > 0 ? `+${d}` : `${d}`;
}

export function SummaryModal({ post, me, memberMap, onClose }: Props) {
  const recap = useMemo(() => buildRoundRecap(post, me, memberMap), [post, me, memberMap]);
  const gross = post.round?.gross_total ?? 0;
  const par = post.round?.course?.par ?? 72;
  const courseName = post.round?.course?.name || post.metadata?.courseName || 'Course';
  const player = me.name?.split(' ')[0] || 'You';

  const copyText = () => {
    const full = `${recap.subject}\n\n${recap.body}`;
    void navigator.clipboard?.writeText(full);
  };

  const sendEmail = () => {
    const subject = encodeURIComponent(recap.subject);
    const body = encodeURIComponent(`${recap.body}\n\n— Sent from Bag Tag`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sendText = () => {
    const body = encodeURIComponent(`${recap.subject}\n\n${recap.body}`);
    window.location.href = `sms:?body=${body}`;
  };

  return (
    <Sheet title="Round Recap" onClose={onClose}>
      <div className="space-y-4 px-4 pb-8">
        <div className="flex items-center gap-4 rounded border border-gold-deep bg-navy p-4 text-cream">
          <div className="font-serif text-5xl font-bold leading-none text-gold">{gross}</div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold tracking-widest text-gold/80">POSTED BY {player.toUpperCase()}</div>
            <div className="mt-1 font-serif text-base font-semibold">{courseName}</div>
            <div className="mt-1 text-xs text-gold/80">
              {scoreLabel(gross, par)} · par {par}
            </div>
          </div>
        </div>

        <div className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 text-[10px] font-bold tracking-wider text-gold-deep">
          <Sparkles size={11} /> RECAP
        </div>

        <div className="rounded border border-line border-l-[3px] border-l-gold bg-cream-warm p-4">
          <div className="font-serif text-sm font-bold text-navy">{recap.subject}</div>
          <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{recap.body}</div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={copyText} className="flex flex-col items-center gap-1 rounded-xl border border-line bg-paper py-3 text-xs font-semibold text-navy">
            <Copy size={16} /> Copy
          </button>
          <button type="button" onClick={sendEmail} className="flex flex-col items-center gap-1 rounded-xl border border-line bg-paper py-3 text-xs font-semibold text-navy">
            <Mail size={16} /> Email
          </button>
          <button type="button" onClick={sendText} className="flex flex-col items-center gap-1 rounded-xl border border-line bg-paper py-3 text-xs font-semibold text-navy">
            <MessageSquare size={16} /> Text
          </button>
        </div>

        <button type="button" onClick={onClose} className="w-full rounded-full bg-navy py-3 text-sm font-bold text-cream">
          Done
        </button>
      </div>
    </Sheet>
  );
}
